export class InputManager {
  private readonly keys = new Set<string>();
  private readonly pressed = new Set<string>();
  private readonly released = new Set<string>();
  pointerNdc = { x: 0, y: 0 };
  pointerActive = false;
  pointerFire = false;
  touchMove = { x: 0, y: 0 };
  touchAim = { x: 0, y: 0 };
  touchFire = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown, { passive: false });
    window.addEventListener('keyup', this.onKeyUp, { passive: false });
    window.addEventListener('blur', this.clear);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.clear();
    });

    canvas.addEventListener('pointermove', this.onPointerMove, { passive: true });
    canvas.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    window.addEventListener('pointerup', this.onPointerUp, { passive: true });
    window.addEventListener('pointercancel', this.onPointerUp, { passive: true });
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    const code = event.code;
    if (!this.keys.has(code)) this.pressed.add(code);
    this.keys.add(code);
    if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(code)) {
      event.preventDefault();
    }
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
    this.released.add(event.code);
  };

  private updatePointer(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / Math.max(rect.width, 1);
    const y = (event.clientY - rect.top) / Math.max(rect.height, 1);
    this.pointerNdc.x = x * 2 - 1;
    this.pointerNdc.y = -(y * 2 - 1);
    this.pointerActive = true;
  }

  private onPointerMove = (event: PointerEvent): void => this.updatePointer(event);

  private onPointerDown = (event: PointerEvent): void => {
    this.updatePointer(event);
    if (event.button === 0) {
      this.pointerFire = true;
      event.preventDefault();
    }
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (event.button === 0) this.pointerFire = false;
  };

  isDown(...codes: string[]): boolean {
    return codes.some((code) => this.keys.has(code));
  }

  wasPressed(code: string): boolean {
    return this.pressed.has(code);
  }

  wasReleased(code: string): boolean {
    return this.released.has(code);
  }

  consumePressed(code: string): boolean {
    const value = this.pressed.has(code);
    this.pressed.delete(code);
    return value;
  }

  setTouchMove(x: number, y: number): void {
    this.touchMove.x = Math.max(-1, Math.min(1, x));
    this.touchMove.y = Math.max(-1, Math.min(1, y));
  }

  setTouchAim(x: number, y: number): void {
    this.touchAim.x = Math.max(-1, Math.min(1, x));
    this.touchAim.y = Math.max(-1, Math.min(1, y));
  }

  setTouchFire(active: boolean): void {
    this.touchFire = active;
  }

  endFrame(): void {
    this.pressed.clear();
    this.released.clear();
  }

  clear = (): void => {
    this.keys.clear();
    this.pressed.clear();
    this.released.clear();
    this.pointerFire = false;
    this.touchMove.x = 0;
    this.touchMove.y = 0;
    this.touchAim.x = 0;
    this.touchAim.y = 0;
    this.touchFire = false;
  };

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }
}
